import { Injectable, Injector } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { noop, times } from '@sama/micro-dash';
import { from } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { vectorTaggingToolsConfig } from '../../../../cross-team-shared/tools.config';
import {
  Point2D,
  ShapeLocation,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { getShapeHelper } from '../../shape-helpers/get-shape-helper';
import { AbstractShapeTool2D } from '../shared/abstract-shape-tool-2d';
import { CrosshairService } from '../shared/crosshair.service';
import {
  MultiClickShapeTool,
  MultiClickShapeToolHelper,
} from '../shared/tool-helpers/multi-click-shape-tool-helper';
import { RectangleToolHelper } from './rectangle-tool-helper';

@Injectable()
export class RectangleTool
  extends AbstractShapeTool2D
  implements MultiClickShapeTool
{
  readonly name = vectorTaggingToolsConfig.Rectangle.name;
  protected crosshairService: CrosshairService;
  private lastStartingPoint: Point2D = [0, 0];

  constructor(protected override injector: Injector) {
    super(injector);
    this.crosshairService = injector.get(CrosshairService);
  }

  override async onActivate() {
    await super.onActivate();

    this.crosshairService.showCrosshair();
    this.onEvent('container', 'mousedown', this.startShape);
  }

  override onDeactivate() {
    super.onDeactivate();
    this.crosshairService.hideCrosshair();
  }

  override buildFirstKeyLocation(initialValues: ShapeLocation<Point2D>) {
    this.lastStartingPoint = this.getSnappedImagePoint();
    return { ...initialValues, points: times(4, () => this.lastStartingPoint) };
  }

  updateLocationToFollowMouse(location: ShapeLocation<Point2D>) {
    const [x1, y1] = this.lastStartingPoint;
    const [x2, y2] = this.getBoundedImagePoint();
    location.points = getShapeHelper('rectangle').fixPoints([
      [x1, y1],
      [x2, y1],
      [x2, y2],
      [x1, y2],
    ]);
  }

  handleInProgressMouseDown(
    helper: MultiClickShapeToolHelper,
    event: MouseEvent,
  ) {
    const points = helper.getPointsStore().state();
    if (getShapeHelper('rectangle').arePointsValid(points)) {
      void helper.stopListening(event);
    }
  }

  protected startShape() {
    const helper = this.createToolHelper();
    this.registerHelper(helper);
    helper.trackAddInProgressSubpointEvent(0);
    this.setUpCrosshairSubscriptions(helper);
  }

  protected createToolHelper(): MultiClickShapeToolHelper {
    return new RectangleToolHelper(
      this,
      this.store('vectorOverlay'),
      this.injector,
    );
  }

  private updateCrosshairVisibility(pointsLength: number) {
    if (pointsLength <= 4) {
      this.crosshairService.showCrosshair();
    } else {
      this.crosshairService.hideCrosshair();
    }
  }

  private setUpCrosshairSubscriptions(helper: MultiClickShapeToolHelper) {
    const helperCompletion = helper.completion.catch(noop);
    this.subscribeTo(
      helper
        .getPointsStore()
        .$.pipe(filter(isDefined), takeUntil(from(helperCompletion))),
      (points) => {
        this.updateCrosshairVisibility(points.length);
      },
    );
    this.subscribeToWhileActive(from(helperCompletion), () => {
      this.crosshairService.showCrosshair();
    });
  }
}
